import React, { useState } from 'react';
import { FileText, Download, Filter, Calendar, Search, TrendingUp, AlertTriangle, CheckCircle, MessageSquare } from 'lucide-react';
import { AIChatAssistant } from '../components/AIChatAssistant';
interface AuditRecord {
  id: string;
  caseId: string;
  date: string;
  room: string;
  procedure: string;
  status: 'complete' | 'override' | 'incomplete';
  duration: string;
  instruments: number;
  overrides: number;
}
const MOCK_AUDITS: AuditRecord[] = [{
  id: '1',
  caseId: '#SRG-9902',
  date: '2024-01-15 14:30',
  room: 'OR 4',
  procedure: 'Laparoscopic Cholecystectomy',
  status: 'complete',
  duration: '00:48:12',
  instruments: 28,
  overrides: 0
}, {
  id: '2',
  caseId: '#SRG-9901',
  date: '2024-01-15 11:15',
  room: 'OR 2',
  procedure: 'Total Hip Replacement',
  status: 'override',
  duration: '01:22:45',
  instruments: 42,
  overrides: 1
}, {
  id: '3',
  caseId: '#SRG-9900',
  date: '2024-01-15 08:00',
  room: 'OR 1',
  procedure: 'Appendectomy',
  status: 'complete',
  duration: '00:32:18',
  instruments: 18,
  overrides: 0
}, {
  id: '4',
  caseId: '#SRG-9899',
  date: '2024-01-14 16:45',
  room: 'OR 3',
  procedure: 'Cardiac Bypass',
  status: 'complete',
  duration: '02:15:33',
  instruments: 56,
  overrides: 0
}, {
  id: '5',
  caseId: '#SRG-9898',
  date: '2024-01-14 13:20',
  room: 'OR 4',
  procedure: 'Spinal Fusion',
  status: 'override',
  duration: '01:45:22',
  instruments: 38,
  overrides: 2
}];
export function ReportsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'complete' | 'override'>('all');
  const filteredAudits = MOCK_AUDITS.filter(audit => {
    const matchesSearch = audit.caseId.toLowerCase().includes(searchTerm.toLowerCase()) || audit.procedure.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || audit.status === filterStatus;
    return matchesSearch && matchesFilter;
  });
  return <div className="p-6 bg-background min-h-screen" data-id="element-514">
      {/* Header */}
      <div className="mb-8" data-id="element-515">
        <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-516">
          Audit Reports
        </h1>
        <p className="text-text-secondary" data-id="element-517">
          Comprehensive reconciliation history and compliance records
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6 mb-8" data-id="element-518">
        <div className="bg-surface p-6 rounded-card border border-border" data-id="element-519">
          <div className="flex items-center justify-between mb-2" data-id="element-520">
            <span className="text-text-secondary text-sm font-medium" data-id="element-521">
              Total Cases
            </span>
            <FileText className="w-5 h-5 text-info" data-id="element-522" />
          </div>
          <div className="text-3xl font-bold text-text-primary" data-id="element-523">156</div>
          <div className="text-xs text-success mt-1" data-id="element-524">+12 this week</div>
        </div>

        <div className="bg-surface p-6 rounded-card border border-border" data-id="element-525">
          <div className="flex items-center justify-between mb-2" data-id="element-526">
            <span className="text-text-secondary text-sm font-medium" data-id="element-527">
              Completion Rate
            </span>
            <TrendingUp className="w-5 h-5 text-success" data-id="element-528" />
          </div>
          <div className="text-3xl font-bold text-text-primary" data-id="element-529">98.7%</div>
          <div className="text-xs text-success mt-1" data-id="element-530">+2.3% vs last month</div>
        </div>

        <div className="bg-surface p-6 rounded-card border border-border" data-id="element-531">
          <div className="flex items-center justify-between mb-2" data-id="element-532">
            <span className="text-text-secondary text-sm font-medium" data-id="element-533">
              Avg Duration
            </span>
            <Calendar className="w-5 h-5 text-info" data-id="element-534" />
          </div>
          <div className="text-3xl font-bold text-text-primary" data-id="element-535">52m</div>
          <div className="text-xs text-text-secondary mt-1" data-id="element-536">Per case</div>
        </div>

        <div className="bg-surface p-6 rounded-card border border-border" data-id="element-537">
          <div className="flex items-center justify-between mb-2" data-id="element-538">
            <span className="text-text-secondary text-sm font-medium" data-id="element-539">
              Overrides
            </span>
            <AlertTriangle className="w-5 h-5 text-warning" data-id="element-540" />
          </div>
          <div className="text-3xl font-bold text-text-primary" data-id="element-541">8</div>
          <div className="text-xs text-warning mt-1" data-id="element-542">5.1% of cases</div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-surface rounded-card border border-border p-6 mb-6" data-id="element-543">
        <div className="flex items-center gap-4" data-id="element-544">
          <div className="flex-1 relative" data-id="element-545">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" data-id="element-546" />
            <input type="text" placeholder="Search by case ID or procedure..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-surface-2 border border-border rounded-input pl-11 pr-4 py-3 text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-info" data-id="element-547" />
          </div>

          <div className="flex gap-2" data-id="element-548">
            <button onClick={() => setFilterStatus('all')} className={`px-4 py-3 rounded-input font-medium transition-colors ${filterStatus === 'all' ? 'bg-info text-white' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}`} data-id="element-549">
              All Cases
            </button>
            <button onClick={() => setFilterStatus('complete')} className={`px-4 py-3 rounded-input font-medium transition-colors ${filterStatus === 'complete' ? 'bg-success text-white' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}`} data-id="element-550">
              Complete
            </button>
            <button onClick={() => setFilterStatus('override')} className={`px-4 py-3 rounded-input font-medium transition-colors ${filterStatus === 'override' ? 'bg-warning text-white' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}`} data-id="element-551">
              Overrides
            </button>
          </div>

          <button className="px-6 py-3 bg-info hover:bg-blue-500 text-white rounded-input font-bold flex items-center gap-2 transition-colors" data-id="element-552">
            <Download className="w-5 h-5" data-id="element-553" />
            Export All
          </button>
        </div>
      </div>

      {/* Audit Table */}
      <div className="bg-surface rounded-card border border-border overflow-hidden" data-id="element-554">
        <table className="w-full" data-id="element-555">
          <thead className="bg-surface-2 border-b border-border" data-id="element-556">
            <tr data-id="element-557">
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-558">
                Case ID
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-559">
                Date & Time
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-560">
                Room
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-561">
                Procedure
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-562">
                Status
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-563">
                Duration
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-564">
                Instruments
              </th>
              <th className="text-left p-4 text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-565">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-id="element-566">
            {filteredAudits.map(audit => <tr key={audit.id} className="hover:bg-surface-2 transition-colors" data-id="element-567">
                <td className="p-4" data-id="element-568">
                  <span className="font-mono font-bold text-info" data-id="element-569">
                    {audit.caseId}
                  </span>
                </td>
                <td className="p-4 text-text-secondary" data-id="element-570">{audit.date}</td>
                <td className="p-4 text-text-secondary" data-id="element-571">{audit.room}</td>
                <td className="p-4 text-text-primary font-medium" data-id="element-572">
                  {audit.procedure}
                </td>
                <td className="p-4" data-id="element-573">
                  {audit.status === 'complete' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-input text-xs font-medium bg-success/10 text-success border border-success/30" data-id="element-574">
                      <CheckCircle className="w-3 h-3" data-id="element-575" /> Complete
                    </span>}
                  {audit.status === 'override' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-input text-xs font-medium bg-warning/10 text-warning border border-warning/30" data-id="element-576">
                      <AlertTriangle className="w-3 h-3" data-id="element-577" /> Override (
                      {audit.overrides})
                    </span>}
                </td>
                <td className="p-4 font-mono text-text-secondary" data-id="element-578">
                  {audit.duration}
                </td>
                <td className="p-4 text-text-secondary" data-id="element-579">{audit.instruments}</td>
                <td className="p-4" data-id="element-580">
                  <button className="px-3 py-1.5 bg-surface-2 hover:bg-surface text-text-primary rounded-input text-sm font-medium transition-colors flex items-center gap-2 border border-border" data-id="element-581">
                    <FileText className="w-4 h-4" data-id="element-582" />
                    View Report
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* AI Chat Assistant */}
      <AIChatAssistant context="reports" data-id="element-583" />
    </div>;
}